import { DEFAULT_LAYOUT_ID } from '@src/components/AppLayout/Layouts/tokens';
import { useActivateDealMutation } from '@src/components/OMS/ManualDealTradeView/endpoints/activateDealEndpoint';
import { getDealCashflowsRoute } from '@src/containers/Routes/routes';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { OTCDealData } from '@src/types/OTC';
import { isDealActivatable, isDealCancelable, isDealModifiable } from '@src/utils/OTC/otcUtils';
import {
  ACTION,
  agGridMenuSeparator,
  filterByCellValueMenuItem,
  IconName,
  useCallbackRef,
  useJsonModal,
  useMixpanel,
  useSecuritiesContext,
  type AgGridIconButtonProps,
  type AgGridMenuItems,
  type Column,
  type ColumnDef,
  type FilterableProperty,
  type FilterBuilderSide,
  type Security,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, ICellRendererParams, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCancelDealDialog } from '../CancelDealDialog';
import { createDealJsonObject, getDealJsonObject } from '../dealJsonObject';
import { useModifyDealMenuAction } from '../useModifyDealMenuAction';
import { viewDealScheduleMenuItem } from '../viewDealScheduleMenuItem';
import { RepoGridRowMenu } from './RepoGridRowMenu';
import { colIDToFilterBuilderKey } from './useRepoFilter';

interface UseRepoMenuParams {
  openClause?: (property: string | undefined, side?: FilterBuilderSide, value?: string | string[]) => void;
  filterableProperties?: FilterableProperty<string>[];
  openRepoInstrumentInspectorDrawer: (deal: OTCDealData, onlyIfOpen?: boolean) => void;
  layoutId?: string;
}

export function useRepoMenu({
  openClause,
  filterableProperties,
  openRepoInstrumentInspectorDrawer,
  layoutId,
}: UseRepoMenuParams): {
  columns: Column[];
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<OTCDealData>) => AgGridMenuItems;
  dataDisplayCallback: (data: OTCDealData) => unknown;
  dialogs: ReactNode;
} {
  const { securitiesBySymbol } = useSecuritiesContext();
  const { handleClickJson, jsonModal } = useJsonModal<{ repo: OTCDealData; security: Security | undefined }>();
  const { handleModifyDeal, modifyDealDialog } = useModifyDealMenuAction();
  const handleCancelDeal = useCancelDealDialog();
  const [activateDeal] = useActivateDealMutation();
  const mixpanel = useMixpanel();
  const { isAuthorized } = useRoleAuth();
  const { enableDealCashflows } = useFeatureFlag();
  const navigate = useNavigate();
  const canSubmitDeal = isAuthorized(ACTION.SUBMIT_DEAL);
  const canCancelDeal = isAuthorized(ACTION.CANCEL_DEAL);

  const handleViewSchedule = useCallbackRef((dealID: string) => {
    navigate(getDealCashflowsRoute({ dealID, layoutId: layoutId ?? DEFAULT_LAYOUT_ID }));
  });

  const createCustomJsonObject = useCallbackRef((repo?: OTCDealData) => {
    createDealJsonObject({
      deal: repo,
      propertyName: 'repo',
      securitiesBySymbol,
      handleClickJson,
    });
  });

  const dataDisplayCallback = useCallbackRef((repo: OTCDealData) =>
    getDealJsonObject({
      deal: repo,
      propertyName: 'repo',
      securitiesBySymbol,
    })
  );

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<OTCDealData>) => {
    const data = params.node?.data;
    const isCancelable = data && isDealCancelable(data);
    const canModifyRepo = data && isDealModifiable(data);
    const isActivatable = data && isDealActivatable(data) && data.ID != null;

    const filterItems: AgGridMenuItems = [];
    if (openClause) {
      const filterMenuItems = filterByCellValueMenuItem({
        params,
        filterableProperties: filterableProperties || [],
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      });
      filterItems.push(
        ...filterMenuItems.filter(
          (item): item is MenuItemDef<OTCDealData> => item !== agGridMenuSeparator && item !== 'separator'
        )
      );
    }
    const menuItems: AgGridMenuItems = compact([
      ...filterItems,
      ...(filterItems.length > 0 ? [agGridMenuSeparator] : []),
      {
        name: 'View Instrument Details',
        action: () => {
          if (data) {
            openRepoInstrumentInspectorDrawer(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.InformationCircle}"/>`,
      },
      viewDealScheduleMenuItem({
        deal: data,
        enabled: enableDealCashflows,
        name: 'View Repo Schedule',
        onViewSchedule: handleViewSchedule,
      }),
    ]);

    const secondaryActions: MenuItemDef[] = [];
    if (canModifyRepo && canSubmitDeal) {
      secondaryActions.push({
        name: 'Modify Repo',
        action: () => {
          if (data) {
            handleModifyDeal(data);
          }
        },
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
      });
    }
    if (isCancelable && canCancelDeal) {
      secondaryActions.push({
        name: 'Cancel Repo',
        action: () => {
          handleCancelDeal(data);
        },
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      });
    }
    if (isActivatable && canSubmitDeal) {
      secondaryActions.push({
        name: 'Activate Repo',
        action: () => {
          if (data?.ID != null) {
            void activateDeal({ dealId: data.ID });
          }
        },
        icon: `<i class="ag-icon ${IconName.CheckCircle}"/>`,
      });
    }
    if (secondaryActions.length > 0) {
      menuItems.push(agGridMenuSeparator, ...secondaryActions);
    }

    return menuItems;
  });

  const renderRepoMenuItems = useCallback(
    (params: ICellRendererParams<OTCDealData>) => <RepoGridRowMenu {...params} onShowJson={createCustomJsonObject} />,
    [createCustomJsonObject]
  );

  const columns = useMemo(
    () =>
      [
        {
          type: 'iconButton',
          id: 'show-more-details',
          frozen: true,
          pinned: 'right',
          suppressColumnsToolPanel: true,
          width: 40,
          params: {
            icon: IconName.Deepdive,
            onClick: params => {
              const data: OTCDealData | undefined = params.node.data;
              if (data) {
                openRepoInstrumentInspectorDrawer(data);
              }
            },
          } satisfies AgGridIconButtonProps,
        },
        {
          type: 'hamburgerMenu',
          id: 'repoMenu',
          width: 40,
          params: {
            renderItems: renderRepoMenuItems,
          },
        },
      ] satisfies ColumnDef<OTCDealData>[],
    [openRepoInstrumentInspectorDrawer, renderRepoMenuItems]
  );

  const dialogs = useMemo(
    () => (
      <>
        {jsonModal}
        {modifyDealDialog}
      </>
    ),
    [jsonModal, modifyDealDialog]
  );

  return {
    columns,
    getCustomContextMenuItems,
    dataDisplayCallback,
    dialogs,
  };
}
